import React, { createContext, useContext, useState, useCallback } from 'react';
import { rowAsDefinition } from './lib/row-definition-search';

const RowFormContext = createContext(null);

export const RowFormProvider = ({ children }) => {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [entity, setEntity] = useState(null);
  const [size, setSize] = useState('medium');
  const [orientation, setOrientation] = useState('horizontal');
  const [showCounter, setShowCounter] = useState(false);
  const [filter, setFilter] = useState({});
  const [cardSort, setCardSort] = useState('personalized');
  const [definition, setDefinition] = useState({});
  const [definitionType, setDefinitionType] = useState('search');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setTitle('');
    setSubtitle('');
    setEntity(null);
    setSize('medium');
    setOrientation('horizontal');
    setShowCounter(false);
    setFilter({});
    setCardSort('personalized');
    setDefinition({});
    setDefinitionType('search');
    setIsSubmitting(false);
  }, []);

  const initializeForm = useCallback(editingRow => {
    if (editingRow) {
      setTitle(editingRow.title || '');
      setSubtitle(editingRow.subtitle || '');
      setEntity(editingRow.entity || null);
      setSize(editingRow.display?.size || 'medium');
      setOrientation(editingRow.display?.orientation || 'horizontal');
      setShowCounter(editingRow.display?.showCounter || false);
      setFilter(editingRow.filter || {});
      setCardSort(editingRow.cardSort || 'personalized');

      const storedDefinition = typeof editingRow.definition === 'string' ? JSON.parse(editingRow.definition) : editingRow.definition || {};
      const rowDefinitionType = editingRow.definitionType || 'search';

      // Backfill missing definition fields from the row's saved entity/filter.
      const fallbackDefinition = rowDefinitionType === 'search' ? rowAsDefinition(editingRow) : {};
      setDefinition({
        ...storedDefinition,
        entity: storedDefinition.entity ?? fallbackDefinition.entity,
        filterEntity: storedDefinition.filterEntity ?? fallbackDefinition.filterEntity,
        tab: storedDefinition.tab ?? fallbackDefinition.tab,
        poolRefinement: storedDefinition.poolRefinement ?? fallbackDefinition.poolRefinement,
        circleIndex: storedDefinition.circleIndex ?? fallbackDefinition.circleIndex,
        sectionType: storedDefinition.sectionType ?? fallbackDefinition.sectionType,
        selection: storedDefinition.selection ?? fallbackDefinition.selection,
        Entity_id: storedDefinition.Entity_id ?? fallbackDefinition.Entity_id
      });
      setDefinitionType(rowDefinitionType);
    } else {
      setTitle('');
      setSubtitle('');
      setEntity(null);
      setSize('medium');
      setOrientation('horizontal');
      setShowCounter(false);
      setFilter({});
      setCardSort('personalized');
      setDefinition({});
      setDefinitionType('search');
      setIsSubmitting(false);
    }
  }, []);

  return (
    <RowFormContext.Provider
      value={{
        title,
        setTitle,
        subtitle,
        setSubtitle,
        entity,
        setEntity,
        size,
        setSize,
        orientation,
        setOrientation,
        showCounter,
        setShowCounter,
        filter,
        setFilter,
        cardSort,
        setCardSort,
        definition,
        setDefinition,
        definitionType,
        setDefinitionType,
        isSubmitting,
        setIsSubmitting,
        resetForm,
        initializeForm
      }}
    >
      {children}
    </RowFormContext.Provider>
  );
};

export const useRowForm = () => {
  const context = useContext(RowFormContext);
  if (!context) {
    throw new Error('useRowForm must be used within a RowFormProvider');
  }
  return context;
};
